/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Box, Center, Flex, Image, Select, Text } from '@chakra-ui/core';
import _isNil from 'lodash/isNil';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteMatch } from 'react-router-dom';
import { Collections } from '../../../../../firebase/firestore/collections';
import { EngagedCandidate } from '../../../../../firebase/firestore/documents/candidate';
import { Position } from '../../../../../firebase/firestore/documents/position';
import { useDocumentSnapshot } from '../../../../../firebase/firestore/hooks';
import { RemoteConfigKey } from '../../../../../firebase/remoteConfig';
import { useStoreState } from '../../../../../models/hooks';
import colors from '../../../../../styles/colors';
import { ReviewQuestions } from '../../../../administration/topCandidates/reviewQuestions/ReviewQuestions';
import { SelectedTab } from '../../../../administration/topCandidates/TopCandidatesContext';
import Label from '../../../../common/Label/Label';
import { useSelectedEngagedCandidate } from '../../../MessagingViewContext';
import { useSeekerContainerContext } from '../../SeekerContainerContext';
import { FreeFormQuestions } from '../freeFormQuestions/FreeFormQuestions';
import useQuestions from '../freeFormQuestions/useFreeFormQuestions';
import { useMCQQuestions } from '../freeFormQuestions/useMCQQuestions';
import useVideoResponseQuestions from '../freeFormQuestions/useVideoResponseQuestions';
import { MultipleChoiceQuestionView } from '../multipleChoiceQuestions/MultipleChoiceQuestionView';
import { SpecificQuestions } from '../specificQuestions/SpecificQuestions';
import useSpecificQuestions, { QuestionStateEnum } from '../specificQuestions/useSpecificQuestions';
import { VideoResponseQuestionsView } from '../videoResponseQuestions/VideoResponseQuestionsView';
import { QuestionType } from './QuestionType';
import useDocumentUploadQuestions from '../freeFormQuestions/useDocumentUploadQuestions';
import DocumentUploadQuestionsView from '../documentUploadQuestions/DocumentUploadQuestionsView';

export type QuestionsProps = {
  handleActionAfterEngage?: (seekerId: string) => void;
  candidateFromTopCandidate?: EngagedCandidate | undefined;
  positionId?: string;
};

export const Questions = ({ handleActionAfterEngage, candidateFromTopCandidate, positionId }: QuestionsProps) => {
  const { t } = useTranslation();
  const [activeQuestionType, setCurrentQuestionType] = useState<QuestionType>(QuestionType.ALL_QUESTIONS);
  const currentAccount = useStoreState((state) => state.app.accounts);
  const enableMultipleChoiceFeature: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_MULTIPLE_CHOICE_FEATURE];
  });
  const { selectedCandidate } = useSeekerContainerContext();

  const selectedEngagedCandidate = useSelectedEngagedCandidate();
  const candidate = selectedCandidate || selectedEngagedCandidate || candidateFromTopCandidate;

  const { initialized, questions, state } = useSpecificQuestions(candidate, positionId);
  const { initialized: freeFormQuestionInitialized, questions: freeFormQuestions, state: freeFormState } = useQuestions(
    candidate,
    positionId,
  );
  const {
    initialized: videoResponseQuestionInitialized,
    questions: videoResponseQuestions,
    state: videoResponseState,
  } = useVideoResponseQuestions(candidate, positionId);
  const position = useDocumentSnapshot<Position>(
    Collections.POSITIONS,
    positionId ?? ((candidate?.position ?? positionId) || ''),
  );

  /* istanbul ignore next */
  const noPositionQuestion =
    position?.freeFormQuestions?.length === 0 &&
    position?.multipleChoiceQuestions?.length === 0 &&
    position.specificQuestions?.length === 0 &&
    position.videoUploadQuestions?.length === 0;

  const noSpecificQuestionsOnPosition = position?.specificQuestions?.length === 0;
  const noFreeFormQuestionsOnPosition = position?.freeFormQuestions?.length === 0;
  const noMultipleChoiceQuestionsOnPosition = position?.multipleChoiceQuestions?.length === 0;
  const hasVideoQuestionsOnPosition = !!position?.videoUploadQuestions?.length;

  const noVideoResponseQuestionsOnPosition = !hasVideoQuestionsOnPosition;

  const {
    initialized: documentUploadQuestionInitialized,
    questions: documentUploadQuestions,
    state: documentUploadState,
  } = useDocumentUploadQuestions(candidate, positionId);

  const hasDocumentQuestionsOnPosition =
    !!position?.documentUploadQuestions?.length || (documentUploadQuestions && documentUploadQuestions.getQuestionsCount() > 0);
  const noDocumentUploadQuestionsOnPosition = !hasDocumentQuestionsOnPosition;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  let multipleChoiceQuestionResponses = useMCQQuestions({ candidate, position: position! });
  if (candidate?.monetized) {
    multipleChoiceQuestionResponses = candidate?.multipleChoiceQuestionResponses ?? [];
  }
  const questionsCount = freeFormQuestions?.getQuestionsCount() || 0;

  const match = useRouteMatch(`/position/:positionId/${SelectedTab.TO_REVIEW}`);

  const isPausedRoute = Boolean(match);

  React.useEffect(() => {
    if (isPausedRoute) {
      setCurrentQuestionType(QuestionType.TO_REVIEW_QUESTIONS);
    }
  }, [isPausedRoute]);

  const noQuestionsView = () => {
    return (
      <Center>
        <Flex direction="column" data-testid="NoQuestions" align="center" p={3} textAlign="center">
          <Image src="/questions.svg" w="180px" h="auto" alt="No SpecificQuestions" />
          <Text mt="8">
            <b>
              {t(
                `questions.${
                  candidate?.monetized && !noPositionQuestion ? 'unansweredEngaged' : /* istanbul ignore next */ 'noAnswers'
                }`,
              )}
            </b>
          </Text>
        </Flex>
      </Center>
    );
  };

  const totalSpecificQuestions = questions?.getQuestionsCount() || 0;
  const totalFreeFormQuestions = freeFormQuestions?.getQuestionsCount() || 0;
  const totalMCQuestions = multipleChoiceQuestionResponses?.length || 0;
  const totalVideoQuestions = videoResponseQuestions?.getQuestionsCount() || 0;
  const totalDocumentQuestions = documentUploadQuestions?.getQuestionsCount() || 0;
  const totalQuestions =
    totalSpecificQuestions + totalFreeFormQuestions + totalMCQuestions + totalVideoQuestions + totalDocumentQuestions;
  const totalSpecificQuestionsAnswered = questions?.getTotalAnsweredQuestionsCount() || 0;
  const totalFreeFormQuestionsAnswered = freeFormQuestions?.getAnsweredQuestionsCount() || 0;
  const totalVideoQuestionsAnswered = videoResponseQuestions?.getAnsweredQuestionsCount() || 0;
  const totalDocumentQuestionsAnswered = documentUploadQuestions?.getAnsweredQuestionsCount() || 0;
  // const totalFreeFormQuestionsAnswered = 0;
  const totalMCQuestionsAnswered = multipleChoiceQuestionResponses?.filter((q) => q?.answers?.length > 0).length || 0;
  const totalQuestionsAnswered =
    totalSpecificQuestionsAnswered +
    totalFreeFormQuestionsAnswered +
    totalMCQuestionsAnswered +
    totalVideoQuestionsAnswered +
    totalDocumentQuestionsAnswered;

  const knockoutTotalSpecificQuestions = questions?.getKnockedOutQuestionsCount() || 0;
  const knockoutAnsweredQuestionsCount = questions?.getKnockedOutAnsweredQuestionsCount() || 0;
  const knockoutTotalMCQuestions = multipleChoiceQuestionResponses?.filter((q) => q.knockout)?.length || 0;
  const knockoutTotalMCQuestionsAnswered =
    multipleChoiceQuestionResponses?.filter((q) => q.knockout && q.isAnsweredCorrectly).length || 0;

  const knockoutTotalQuestions = knockoutTotalSpecificQuestions + knockoutTotalMCQuestions;
  const knockoutTotalQuestionsAnswered = knockoutAnsweredQuestionsCount + knockoutTotalMCQuestionsAnswered;

  const hasNoVideoQuestions =
    videoResponseState === QuestionStateEnum.UNDEFINED ||
    (videoResponseState === QuestionStateEnum.READY && totalVideoQuestions === 0);

  const hasNoDocumentQuestions =
    documentUploadState === QuestionStateEnum.UNDEFINED ||
    (documentUploadState === QuestionStateEnum.READY && totalDocumentQuestions === 0);

  return (
    <Box py={2}>
      {activeQuestionType === QuestionType.ALL_QUESTIONS && (
        <Flex justify="flex-start" align="baseline" mb={2}>
          {totalQuestions > 0 && (
            <Label color={colors.gray['500']} colorDarkMode={colors.gray['600']} fontWeight="600">
              {t(
                totalQuestionsAnswered === 1
                  ? /* istanbul ignore next */ 'questions.totalQuestionAnswered'
                  : 'questions.totalQuestionAnswered_plural',
                {
                  count: totalQuestionsAnswered,
                  total: totalQuestions,
                },
              )}
            </Label>
          )}
        </Flex>
      )}

      {activeQuestionType === QuestionType.ALL_QUESTIONS && (
        <Flex justify="flex-start" align="baseline" mb={2}>
          {knockoutTotalQuestions > 0 && (
            <Label color={colors.gray['500']} colorDarkMode={colors.gray['600']} fontWeight="600">
              {t(
                knockoutTotalQuestionsAnswered === 1
                  ? 'questions.knockoutCount'
                  : /* istanbul ignore next */ 'questions.knockoutCount_plural',
                {
                  count: knockoutTotalQuestionsAnswered,
                  total: knockoutTotalQuestions,
                },
              )}
            </Label>
          )}
        </Flex>
      )}

      <Select
        onChange={(e) => setCurrentQuestionType(e.target.value as QuestionType)}
        bg="blue.500"
        color="#fff"
        borderColor="blue.500"
        mr={5}
        mb={2}
        fontSize="sm"
        fontWeight="bold"
        data-testid="filterQuestionType"
        value={activeQuestionType}
      >
        <option value={QuestionType.ALL_QUESTIONS} style={{ color: '#000' }}>
          {t('common:allQuestions.title')}
        </option>
        <option value={QuestionType.SPECIFIC_QUESTIONS} style={{ color: '#000' }}>
          {t('common:questions.title')}
        </option>
        {!!currentAccount?.configuration?.freeFormQuestionEnabled && (
          <option value={QuestionType.FREE_FORM_QUESTIONS} style={{ color: '#000' }}>
            {t('common:freeFormQuestions.title')}
          </option>
        )}
        {enableMultipleChoiceFeature && (
          <option value={QuestionType.MULTIPLE_CHOICE_QUESTIONS} style={{ color: '#000' }}>
            {t('createJobPosition:multipleChoiceQuestions')}
          </option>
        )}
        {hasVideoQuestionsOnPosition && (
          <option value={QuestionType.VIDEO_RESPONSE} style={{ color: '#000' }}>
            {t('common:videoResponseQuestions.title')}
          </option>
        )}
        {!noDocumentUploadQuestionsOnPosition && (
          <option value={QuestionType.DOCUMENT_UPLOAD} style={{ color: '#000' }}>
            {t('common:documentUploadQuestions.title')}
          </option>
        )}
        {isPausedRoute && (
          <option data-testid="toReviewQuestions" value={QuestionType.TO_REVIEW_QUESTIONS} style={{ color: '#000' }}>
            {t('createJobPosition:toReviewQuestions')}
          </option>
        )}
      </Select>

      {/* ── Specific Questions ── */}
      {activeQuestionType === QuestionType.ALL_QUESTIONS &&
        state !== QuestionStateEnum.UNDEFINED &&
        !noSpecificQuestionsOnPosition && (
          <Text fontSize="sm" fontWeight="bold" mt={3} pb={1} mb={3} borderBottom="1px solid" borderColor="gray.50">
            {t('common:questions.title')}
          </Text>
        )}
      {(activeQuestionType === QuestionType.SPECIFIC_QUESTIONS ||
        (activeQuestionType === QuestionType.ALL_QUESTIONS && !noSpecificQuestionsOnPosition)) && (
        <SpecificQuestions
          activeQuestionType={activeQuestionType}
          initialized={initialized}
          questions={questions}
          state={state}
          candidate={candidate}
          hasNoQuestionOnPosition={noSpecificQuestionsOnPosition}
        />
      )}

      {/* ── Free-form Questions ── */}
      {activeQuestionType === QuestionType.ALL_QUESTIONS &&
        !!currentAccount?.configuration?.freeFormQuestionEnabled &&
        (freeFormState !== QuestionStateEnum.UNDEFINED || questionsCount !== 0) && (
          <Text fontSize="sm" fontWeight="bold" mt={3} pb={1} mb={3} borderBottom="1px solid" borderColor="gray.50">
            {t('common:freeFormQuestions.title')}
          </Text>
        )}
      {((!!currentAccount?.configuration?.freeFormQuestionEnabled && activeQuestionType === QuestionType.FREE_FORM_QUESTIONS) ||
        activeQuestionType === QuestionType.ALL_QUESTIONS) && (
        <FreeFormQuestions
          activeQuestionType={activeQuestionType}
          initialized={freeFormQuestionInitialized}
          questions={freeFormQuestions}
          state={freeFormState}
          candidate={candidate}
          hasNoQuestionOnPosition={noFreeFormQuestionsOnPosition}
        />
      )}

      {/* ── Multiple Choice Questions ── */}
      {activeQuestionType === QuestionType.ALL_QUESTIONS &&
        enableMultipleChoiceFeature &&
        !_isNil(multipleChoiceQuestionResponses) &&
        multipleChoiceQuestionResponses &&
        multipleChoiceQuestionResponses?.length > 0 && (
          <Text fontSize="sm" fontWeight="bold" mt={3} pb={1} mb={3} borderBottom="1px solid" borderColor="gray.50">
            {t('createJobPosition:multipleChoiceQuestions')}
          </Text>
        )}

      {((enableMultipleChoiceFeature && activeQuestionType === QuestionType.MULTIPLE_CHOICE_QUESTIONS) ||
        activeQuestionType === QuestionType.ALL_QUESTIONS) && (
        <MultipleChoiceQuestionView
          questions={multipleChoiceQuestionResponses}
          activeQuestionType={activeQuestionType}
          candidate={candidate}
          hasNoQuestionOnPosition={noMultipleChoiceQuestionsOnPosition}
        />
      )}

      {/* ── Video Response Questions ── */}
      {activeQuestionType === QuestionType.ALL_QUESTIONS &&
        !noVideoResponseQuestionsOnPosition &&
        !_isNil(videoResponseQuestions) &&
        videoResponseState !== QuestionStateEnum.UNDEFINED &&
        videoResponseQuestions?.getQuestionsCount() > 0 && (
          <Text fontSize="sm" fontWeight="bold" mt={3} pb={1} mb={3} borderBottom="1px solid" borderColor="gray.50">
            {t('common:videoResponseQuestions.title')}
          </Text>
        )}

      {((!noVideoResponseQuestionsOnPosition && activeQuestionType === QuestionType.VIDEO_RESPONSE) ||
        activeQuestionType === QuestionType.ALL_QUESTIONS) && (
        <VideoResponseQuestionsView
          questions={videoResponseQuestions}
          initialized={videoResponseQuestionInitialized}
          activeQuestionType={activeQuestionType}
          hasNoQuestionOnPosition={noVideoResponseQuestionsOnPosition}
        />
      )}

      {/* ── Document Upload Questions ── */}
      {activeQuestionType === QuestionType.ALL_QUESTIONS &&
        !noDocumentUploadQuestionsOnPosition &&
        documentUploadQuestions &&
        documentUploadQuestions.getQuestionsCount() > 0 && (
          <Text fontSize="sm" fontWeight="bold" mt={3} pb={1} mb={3} borderBottom="1px solid" borderColor="gray.50">
            {t('common:documentUploadQuestions.title')}
          </Text>
        )}

      {((!noDocumentUploadQuestionsOnPosition && activeQuestionType === QuestionType.DOCUMENT_UPLOAD) ||
        activeQuestionType === QuestionType.ALL_QUESTIONS) && (
        <DocumentUploadQuestionsView
          questions={documentUploadQuestions}
          initialized={documentUploadQuestionInitialized}
          activeQuestionType={activeQuestionType}
          hasNoQuestionOnPosition={noDocumentUploadQuestionsOnPosition}
        />
      )}

      {activeQuestionType === QuestionType.TO_REVIEW_QUESTIONS && (
        <ReviewQuestions
          selectedCandidate={candidate}
          handleActionAfterEngage={(seekerId: string) => {
            handleActionAfterEngage?.(seekerId);
          }}
        />
      )}

      {activeQuestionType === QuestionType.ALL_QUESTIONS &&
        (freeFormState === QuestionStateEnum.UNDEFINED || (freeFormState === QuestionStateEnum.READY && questionsCount === 0)) &&
        (state === QuestionStateEnum.UNDEFINED || (state === QuestionStateEnum.READY && totalSpecificQuestions === 0)) &&
        (_isNil(candidate?.multipleChoiceQuestionResponses) ||
          (candidate?.multipleChoiceQuestionResponses && candidate?.multipleChoiceQuestionResponses?.length <= 0)) &&
        hasNoVideoQuestions &&
        hasNoDocumentQuestions &&
        noQuestionsView()}
    </Box>
  );
};
